import { FormEvent, useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PhoneInput } from "../components/ui/PhoneInput";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../components/ui/use-toast";
import { useTranslation } from "react-i18next";

const ProfilePage = () => {
  const { user, updateProfile, updatePreferences, changePassword } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    phone: user?.phone ?? "",
  });
  const [preferences, setPreferences] = useState({
    notifyEmail: user?.notifyEmail ?? true,
    notifySms: user?.notifySms ?? false,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const isPasswordValid = useMemo(
    () =>
      passwordForm.newPassword.length >= 8 &&
      passwordForm.newPassword === passwordForm.confirmPassword,
    [passwordForm.confirmPassword, passwordForm.newPassword]
  );

  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone ?? "",
      });
      setPreferences({
        notifyEmail: user.notifyEmail,
        notifySms: user.notifySms,
      });
    }
  }, [user]);

  if (!user) {
    return <p>{t("profile.loginRequired")}</p>;
  }

  const handleProfileSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile(profileForm);
      toast({
        title: t("profile.updateSuccessTitle"),
        description: t("profile.updateSuccessDescription"),
      });
    } catch (err) {
      toast({
        title: t("profile.updateErrorTitle"),
        description:
          (err as string) ?? t("profile.updateErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePreferencesChange = async (nextPrefs: {
    notifyEmail: boolean;
    notifySms: boolean;
  }) => {
    setPreferences(nextPrefs);
    setSavingPreferences(true);
    try {
      await updatePreferences(nextPrefs);
      toast({ title: t("profile.preferencesSavedTitle") });
    } catch (err) {
      toast({
        title: t("profile.preferencesErrorTitle"),
        description:
          (err as string) ?? t("profile.preferencesErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setSavingPreferences(false);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isPasswordValid) {
      toast({
        title: t("profile.passwordInvalidTitle"),
        description: t("profile.passwordInvalidDescription"),
        variant: "destructive",
      });
      return;
    }
    setSavingPassword(true);
    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast({
        title: t("profile.passwordUpdateSuccessTitle"),
        description: t("profile.passwordUpdateSuccessDescription"),
      });
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      toast({
        title: t("profile.passwordUpdateErrorTitle"),
        description:
          (err as string) ?? t("profile.passwordUpdateErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <section className="space-y-6 rounded-3xl border border-border bg-card p-6 text-foreground shadow-lg dark:border-white/10 dark:bg-white/5 dark:text-white dark:shadow-2xl">
      <div>
        <h2 className="text-3xl font-semibold">{t("profile.title")}</h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t("profile.subtitle")}
        </p>
      </div>

      <form
        onSubmit={handleProfileSubmit}
        className="grid gap-4 rounded-3xl border border-border bg-muted/40 p-6 md:grid-cols-2 dark:border-white/10 dark:bg-black/30">
        <div className="space-y-2">
          <Label htmlFor="firstName">{t("profile.firstNameLabel")}</Label>
          <Input
            id="firstName"
            value={profileForm.firstName}
            onChange={(event) =>
              setProfileForm((prev) => ({
                ...prev,
                firstName: event.target.value,
              }))
            }
            disabled={savingProfile}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">{t("profile.lastNameLabel")}</Label>
          <Input
            id="lastName"
            value={profileForm.lastName}
            onChange={(event) =>
              setProfileForm((prev) => ({
                ...prev,
                lastName: event.target.value,
              }))
            }
            disabled={savingProfile}
            required
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="phone">{t("profile.phoneLabel")}</Label>
          <PhoneInput
            id="phone"
            value={profileForm.phone}
            onChange={(value) =>
              setProfileForm((prev) => ({
                ...prev,
                phone: value,
              }))
            }
            placeholder={t("profile.phonePlaceholder")}
            disabled={savingProfile}
            required
          />
        </div>
        <div className="md:col-span-2">
          <Button type="submit" disabled={savingProfile}>
            {savingProfile ? t("profile.saving") : t("profile.updateButton")}
          </Button>
        </div>
      </form>

      <div className="rounded-3xl border border-border bg-muted/40 p-6 dark:border-white/10 dark:bg-black/30">
        <h3 className="text-lg font-semibold">{t("profile.notificationsTitle")}</h3>
        <div className="mt-4 space-y-3">
          <label className="flex items-center justify-between rounded-2xl border border-border bg-muted/50 px-4 py-3 text-sm dark:border-white/10 dark:bg-white/5">
            <span>{t("profile.notificationsEmail")}</span>
            <Checkbox
              checked={preferences.notifyEmail}
              onCheckedChange={(checked) =>
                handlePreferencesChange({
                  ...preferences,
                  notifyEmail: Boolean(checked),
                })
              }
              disabled={savingPreferences}
            />
          </label>
          <label className="flex items-center justify-between rounded-2xl border border-border bg-muted/50 px-4 py-3 text-sm dark:border-white/10 dark:bg-white/5">
            <span>{t("profile.notificationsSms")}</span>
            <Checkbox
              checked={preferences.notifySms}
              onCheckedChange={(checked) =>
                handlePreferencesChange({
                  ...preferences,
                  notifySms: Boolean(checked),
                })
              }
              disabled={savingPreferences}
            />
          </label>
        </div>
      </div>

      <form
        onSubmit={handlePasswordSubmit}
        className="space-y-4 rounded-3xl border border-border bg-muted/40 p-6 dark:border-white/10 dark:bg-white/5">
        <h3 className="text-lg font-semibold text-foreground dark:text-white">
          {t("profile.changePasswordTitle")}
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">{t("profile.currentPasswordLabel")}</Label>
            <Input
              id="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(event) =>
                setPasswordForm((prev) => ({
                  ...prev,
                  currentPassword: event.target.value,
                }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">{t("profile.newPasswordLabel")}</Label>
            <Input
              id="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={(event) =>
                setPasswordForm((prev) => ({
                  ...prev,
                  newPassword: event.target.value,
                }))
              }
              required
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="confirmPassword">{t("profile.confirmPasswordLabel")}</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(event) =>
                setPasswordForm((prev) => ({
                  ...prev,
                  confirmPassword: event.target.value,
                }))
              }
              required
            />
          </div>
        </div>
        <Button
          type="submit"
          variant="secondary"
          disabled={savingPassword || !isPasswordValid}>
          {savingPassword ? t("profile.passwordUpdating") : t("profile.passwordUpdateButton")}
        </Button>
      </form>
    </section>
  );
};

export default ProfilePage;
